/* eslint-disable react/prop-types */
import * as React from 'react';
import * as AvatarPrimitive from '@radix-ui/react-avatar';

import { twMerge } from 'tailwind-merge';

import Typography from 'components-new/typography/typography';

export interface AvatarProps {
  name?: string;
  image?: string;
  className?: string;
}

const getInitials = (name: string) => {
  const names = name.split(' ');
  let initials = names[0].substring(0, 1).toUpperCase();

  if (names.length > 1) {
    initials += names[names.length - 1].substring(0, 1).toUpperCase();
  }
  return initials;
};

const AvatarImage = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Image>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Image
    ref={ref}
    className={twMerge('aspect-square h-full w-full object-cover', className)}
    {...props}
  />
));
AvatarImage.displayName = AvatarPrimitive.Image.displayName;

const AvatarFallback = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Fallback>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Fallback
    ref={ref}
    className={twMerge(
      'flex h-full w-full items-center justify-center rounded-full bg-muted',
      className,
    )}
    {...props}
  />
));
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;

const Avatar = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Root>,
  AvatarProps
>(({ className, name, image, ...props }, ref) => {
  const AvatarClassName = twMerge(
    'relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full',
    'border-2 ml-2 bg-white cursor-pointer',
    'hover:bg-gray-100',
    'focus:outline-none focus:ring-1 focus:ring-primary-200 focus:ring-offset-1 focus:ring-offset-primary-200',
    'focus:border-primary active:border-primary',
    className,
  );
  return (
    <AvatarPrimitive.Root
      role="button"
      tabIndex={0}
      className={AvatarClassName}
      ref={ref}
      {...props}
    >
      <AvatarImage src={image} alt="User Avatar" />
      <AvatarFallback>
        <Typography
          variant="heading-5"
          as="span"
          className="text-gray-500 uppercase"
        >
          {name && getInitials(name)}
        </Typography>
      </AvatarFallback>
    </AvatarPrimitive.Root>
  );
});

Avatar.displayName = 'Avatar';

export default Avatar;
